import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { createVuetify } from 'vuetify'
import { createI18n } from 'vue-i18n'

// Polyfill: Array.findLast for older browsers (iOS Safari < 15.4)
if (!Array.prototype.findLast) {
  Array.prototype.findLast = function (fn, thisArg) {
    for (let i = this.length - 1; i >= 0; i--) {
      if (fn.call(thisArg, this[i], i, this)) return this[i]
    }
  }
}

// Vuetify - Optimized imports
import 'vuetify/styles'
import { aliases, mdi } from 'vuetify/iconsets/mdi'

// Load MDI icons asynchronously after initial render
setTimeout(() => {
  import('@mdi/font/css/materialdesignicons.css')
}, 1000)

// Components
import App from './App.vue'
import router from './router'

// Locales
import kz from './locales/kz.json'
import ru from './locales/ru.json'
import en from './locales/en.json'

// Styles - Load after critical components
// import './styles/main.scss'
import './styles/mobile.css'
import './styles/korkem-theme.scss'
import { korkemTheme } from './themes/korkem.js'

const cleanupLegacyPwa = async () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(registration => registration.unregister()))
    } catch (error) {
      // Ignore legacy PWA cleanup failures
    }
  }

  if ('caches' in window) {
    try {
      const cacheKeys = await caches.keys()
      await Promise.all(
        cacheKeys
          .filter(key =>
            key.includes('workbox') ||
            key.includes('api-cache') ||
            key.includes('image-cache') ||
            key.includes('font-cache')
          )
          .map(key => caches.delete(key))
      )
    } catch (error) {
      // Ignore cache cleanup failures
    }
  }
}

const syncViewportMetrics = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const viewportHeight = Math.round(window.visualViewport?.height || window.innerHeight || 0)

  document.documentElement.style.setProperty('--app-viewport-height', `${viewportHeight}px`)
  document.documentElement.style.setProperty('--app-safe-top', 'env(safe-area-inset-top, 0px)')
  document.documentElement.style.setProperty('--app-safe-bottom', 'env(safe-area-inset-bottom, 0px)')
}

cleanupLegacyPwa()
syncViewportMetrics()
window.addEventListener('resize', syncViewportMetrics, { passive: true })
window.addEventListener('orientationchange', syncViewportMetrics, { passive: true })
window.visualViewport?.addEventListener('resize', syncViewportMetrics, { passive: true })

// Create Vuetify
const vuetify = createVuetify({
  theme: {
    defaultTheme: localStorage.getItem('korkem-auto-theme') || 'dark',
    themes: {
      dark: {
        colors: {
          primary: '#FFC107',
          secondary: '#FF9800',
          accent: '#FFEB3B',
          error: '#F44336',
          warning: '#FF9800',
          info: '#2196F3',
          success: '#4CAF50',
          background: '#1a1a1a',
          surface: '#2d2d2d',
        },
      },
      light: {
        colors: {
          primary: '#FFC107',
          secondary: '#FF9800',
          accent: '#FFEB3B',
          error: '#F44336',
          warning: '#FF9800',
          info: '#2196F3',
          success: '#4CAF50',
        },
      },
      // Second theme — logo colors (deep blue, light base). Defined in src/themes/korkem.js
      korkem: korkemTheme,
    },
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    },
  },
  // Performance optimizations
  defaults: {
    VBtn: {
      style: 'text-transform: none;',
    },
    VCard: {
      flat: true,
    },
  },
})

// Create i18n
const i18n = createI18n({
  legacy: false,
  locale: localStorage.getItem('korkem-auto-locale') || 'kz',
  fallbackLocale: 'kz',
  messages: {
    kz,
    ru,
    en,
  },
})

// Create Pinia
const pinia = createPinia()

// Create app
const app = createApp(App)

// Initialize error tracking
import { initErrorTracking, getVueErrorHandler } from './services/errorTracker'
initErrorTracking()

// Set Vue error handler
app.config.errorHandler = (err, vm, info) => {
  console.error('Vue Error:', err)
  const vueHandler = getVueErrorHandler()
  if (vueHandler) {
    vueHandler(err, vm, info)
  }
}

// Initialize auth state before mounting
import { useAuthStore } from './stores/auth'

// Create app and configure plugins first
app.use(pinia)
app.use(vuetify)
app.use(i18n)

// Initialize auth BEFORE router
const authStore = useAuthStore()

// Initialize authentication on app start
authStore.initAuth().then(() => {
  // Only add router AFTER auth is initialized
  app.use(router)
  app.mount('#app')

  // Remove loading screen when app is mounted
  nextTick(() => {
    syncViewportMetrics()
    const loadingScreen = document.querySelector('.loading-screen')
    if (loadingScreen) {
      loadingScreen.style.opacity = '0'
      loadingScreen.style.transition = 'opacity 0.5s ease-out'
      setTimeout(() => {
        loadingScreen.remove()
      }, 500)
    }
  })
})
